// ============================================================
// MongoDBPlayground.jsx
//
// A branded, interactive sandbox environment guide for testing
// MongoDB queries and scripts safely.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #00E0FF  (Cyan / Logic)
//   Text        → #FFFFFF  (White)
//   Terminal    → #010409  (High-Contrast Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Button,
    Avatar,
    Tooltip,
} from "@mui/material";

/* MUI Icons */
import TerminalIcon from "@mui/icons-material/Terminal";
import ScienceIcon from "@mui/icons-material/Science";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BugReportIcon from "@mui/icons-material/BugReport";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    cyan: "#00E0FF",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    terminalBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeSnippet({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.terminalBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBPlayground() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Interactive Lab · Sandbox"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        MongoDB <span style={{ color: BRAND.green }}>Playground</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        The playground is your safe space to test complex queries, scripts, and aggregations before
                        pushing them to production. Experiment with BSON, validate schemas, and master the shell.
                    </Typography>
                </Box>

                {/* 2. PLAYGROUND FEATURES */}
                <Box sx={{ mb: 10 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Avatar sx={{ bgcolor: `${BRAND.green}15`, color: BRAND.green, mb: 2 }}>
                                    <ScienceIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>Safe Iteration</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Test <code>updateMany</code> or <code>deleteMany</code> operations on dummy data without risking your main database.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Avatar sx={{ bgcolor: `${BRAND.cyan}15`, color: BRAND.cyan, mb: 2 }}>
                                    <CodeIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>Script Validation</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Write and debug JavaScript-based <code>mongosh</code> scripts to automate data migrations or cleanup.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.05)", color: BRAND.white, mb: 2 }}>
                                    <BugReportIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>Explain Queries</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Use <code>.explain("executionStats")</code> to see how your query performs and if it uses the right indexes.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. SIMULATED SHELL SESSIONS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <TerminalIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Common Sandbox Scenarios</Typography>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ color: BRAND.green, mb: 2, fontWeight: 700 }}>Scenario 1: Bulk Test Data</Typography>
                            <CodeSnippet
                                label="generate_data.js"
                                code={`// Populate 'test' collection with 100 docs\nconst docs = [];\nfor (let i = 1; i <= 100; i++) {\n  docs.push({\n    item: "product_" + i,\n    qty: Math.floor(Math.random() * 500),\n    category: (i % 2 === 0) ? "A" : "B"\n  });\n}\ndb.test.insertMany(docs);`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ color: BRAND.cyan, mb: 2, fontWeight: 700 }}>Scenario 2: Complex Aggregation</Typography>
                            <CodeSnippet
                                label="aggregate_test.js"
                                color={BRAND.cyan}
                                code={`// Find avg quantity per category\ndb.test.aggregate([\n  { $match: { qty: { $gt: 100 } } },\n  { $group: { \n      _id: "$category", \n      avgQty: { $avg: "$qty" } \n    }\n  },\n  { $sort: { avgQty: -1 } }\n]);`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. PERFORMANCE PROFILING */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <PlayCircleFilledIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Profiling Your Code</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        A "Playground" is the best place to profile your queries. Running <code>.explain()</code>
                        helps you see the <strong>Winning Plan</strong> chosen by the MongoDB query optimizer.
                    </Typography>



                    <CodeSnippet
                        label="Performance Check"
                        code={`db.users.find({ email: "test@test.com" }).explain("executionStats");\n\n// Look for: "totalDocsExamined": 1 (Success!)\n// Avoid: "COLLSCAN" (Bad for large datasets!)`}
                    />
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <RestartAltIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Finished Experimenting?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Clean your playground environment before starting a new module.
                        Use <code>db.dropDatabase()</code> to reset.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Reset Environment</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Next Module</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
